import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { doc as fsDoc, getDoc } from "firebase/firestore";
import logoImage from "../logokye.png";
import logo from "../logokye.png";
import { db } from "../Firebase/firebaseconfig";
import { metodoPagoLabel } from "./metodosPago";
import { toJsDate } from "./fechas";
const n = (v) => Number(v || 0);

// ── Helpers Constancia de Liquidación ─────────────────────────────────
// Convierte enteros 0–99 a su nombre en español. Suficiente para manzanas
// y lotes; la versión manual de la constancia escribe "Manzana 7 siete".
const UNIDADES_ES = [
  "cero", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve",
  "diez", "once", "doce", "trece", "catorce", "quince", "dieciséis", "diecisiete",
  "dieciocho", "diecinueve",
];
const DECENAS_ES = [
  "", "", "veinte", "treinta", "cuarenta", "cincuenta",
  "sesenta", "setenta", "ochenta", "noventa",
];
const numeroEnLetras = (num) => {
  const v = Math.floor(Number(num) || 0);
  if (v < 0 || v > 99) return String(v);
  if (v < 20) return UNIDADES_ES[v];
  if (v % 10 === 0) return DECENAS_ES[Math.floor(v / 10)];
  if (v < 30) return `veinti${UNIDADES_ES[v - 20]}`;
  return `${DECENAS_ES[Math.floor(v / 10)]} y ${UNIDADES_ES[v % 10]}`;
};

// Agrupa propiedades por manzana → lotes ordenados; devuelve frase tipo:
//   "lote Número 5 cinco de la Manzana 7 siete"
//   "lotes Número 5, 7, 9 de la Manzana 7 siete"
//   "lote N° 3 de la Manzana 2 dos y lotes N° 5, 7 de la Manzana 4 cuatro"
const describirPropiedadesPorManzana = (props = []) => {
  const grupos = new Map();
  for (const p of props) {
    const mz = p.manzana ?? p.mz ?? "";
    const lt = p.lote ?? p.lt ?? "";
    if (!mz && !lt) continue;
    const key = String(mz);
    if (!grupos.has(key)) grupos.set(key, []);
    grupos.get(key).push(lt);
  }
  if (!grupos.size) return "";

  const partes = [];
  for (const [mz, lotes] of grupos) {
    const lotesOrdenados = lotes
      .map((l) => Number(l))
      .filter((l) => Number.isFinite(l))
      .sort((a, b) => a - b);
    const listaLotes = lotesOrdenados.join(", ");
    const palabraLote = lotesOrdenados.length > 1 ? "lotes Número" : "lote Número";
    const mzNum = Number(mz);
    const mzLetras = Number.isFinite(mzNum) ? ` ${numeroEnLetras(mzNum)}` : "";
    partes.push(`${palabraLote} ${listaLotes} de la Manzana ${mz}${mzLetras}`);
  }
  return partes.join(", y ");
};

// Resuelve el fraccionamiento de una deuda y devuelve {nombre, ubicacion}.
// Lee de firestore solo si hay fraccionamientoId; tolera fallos.
const resolverFraccionamientoConUbicacion = async (deuda) => {
  const props = Array.isArray(deuda?.propiedades) ? deuda.propiedades : [];
  const fraccId =
    deuda?.fraccionamientoId ||
    props.find((p) => p?.fraccionamientoId)?.fraccionamientoId ||
    "";
  const fraccNombreDoc =
    deuda?.fraccionamientoNombre ||
    deuda?.fraccNombre ||
    props.find((p) => p?.fraccionamientoNombre || p?.fraccNombre)?.fraccionamientoNombre ||
    props.find((p) => p?.fraccNombre)?.fraccNombre ||
    "";

  if (!fraccId) {
    return { nombre: fraccNombreDoc, ubicacion: "" };
  }
  try {
    const snap = await getDoc(fsDoc(db, "fraccionamientos", fraccId));
    if (!snap.exists()) return { nombre: fraccNombreDoc, ubicacion: "" };
    const f = snap.data();
    return {
      nombre: f?.nombre || fraccNombreDoc,
      ubicacion: f?.ubicacion || "",
    };
  } catch {
    return { nombre: fraccNombreDoc, ubicacion: "" };
  }
};

// CV-1: PDFs de cliente (estado de cuenta, comprobante, ticket, constancia)
// siempre con 2 decimales. Los centavos importan para conciliación con el cliente.
const money = (v) =>
  `$${n(v).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const signedMoney = (v) => {
  const value = n(v);
  return value < 0 ? `-${money(Math.abs(value))}` : money(value);
};

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = logoImage;
  });

// Soporta cualquier forma de fecha: Firestore Timestamp, Date, string ISO,
// {seconds, nanoseconds}, número ms. Crítico tras el restore desde prod (las
// fechas vienen como strings ISO y `.toDate()` ya no aplica).
const fecha = (ts) => {
  const d = toJsDate(ts);
  return d ? d.toLocaleDateString("es-MX") : "—";
};

const fechaHora = (ts) => {
  const d = toJsDate(ts);
  return d ? d.toLocaleString("es-MX") : "—";
};

const fechaContrato = (deuda = {}) => {
  const value =
    deuda?.fechaInicio ||
    deuda?.fechaInicioDeuda ||
    deuda?.fechaContrato ||
    deuda?.fechaVenta ||
    deuda?.creada ||
    deuda?.createdAt;
  const d = toJsDate(value);
  return d ? d.toLocaleDateString("es-MX") : "-";
};

const clienteNombre = (cliente = {}) =>
  `${cliente?.nombre || ""} ${cliente?.apellidos || ""}`.trim() || "—";

const getPropsList = (deuda = {}) => {
  if (Array.isArray(deuda?.propiedades) && deuda.propiedades.length) {
    return deuda.propiedades;
  }

  if (
    deuda?.propiedadNombre ||
    deuda?.propiedadFolio ||
    deuda?.propiedadCodigo ||
    deuda?.manzana ||
    deuda?.lote
  ) {
    return [
      {
        propiedadId: deuda.propiedadId,
        folio: deuda.propiedadFolio,
        codigo: deuda.propiedadCodigo,
        nombre: deuda.propiedadNombre,
        fraccionamientoId: deuda.fraccionamientoId,
        manzana: deuda.manzana,
        lote: deuda.lote,
      },
    ];
  }

  return [];
};

const propsLabel = (deuda = {}) => {
  const props = getPropsList(deuda);

  if (!props.length) return "—";

  return props
    .map((p) => {
      const cod =
        p.codigo ||
        (p.manzana || p.lote ? `MZ${p.manzana || "?"}-LT${p.lote || "?"}` : "");
      const folio = p.folio || "";
      const nombre = p.nombre || "";
      return [cod, folio, nombre].filter(Boolean).join(" ");
    })
    .join(" · ");
};

const isDevolucionDeuda = (deuda = {}) => {
  const estado = String(deuda?.estado || "").toLowerCase();
  const estatus = String(deuda?.estatus || "").toLowerCase();

  return (
    deuda?.devolucion === true ||
    deuda?.esDevolucion === true ||
    estado.includes("devolucion") ||
    estado.includes("devuelta") ||
    estatus.includes("cancel")
  );
};

const computeFinanzas = (deuda = {}) => {
  const montoTotal = n(deuda?.monto ?? deuda?.importe);
  const pagos = Array.isArray(deuda?.pagos) ? deuda.pagos : [];

  let totalPagadoVenta = 0;
  let totalDevuelto = 0;
  let engancheEnPagosAcum = 0;
  let mensualidadesAcum = 0;

  pagos.forEach((p) => {
    const cantidad = n(p?.cantidad);
    const tipo = String(p?.tipo || "").toLowerCase();

    if (cantidad > 0) {
      totalPagadoVenta += cantidad;
      if (tipo === "enganche") engancheEnPagosAcum += cantidad;
      else if (tipo !== "ajuste_devolucion" && tipo !== "ajuste") mensualidadesAcum += cantidad;
    }
    if (cantidad < 0 || tipo === "devolucion_pago") totalDevuelto += Math.abs(cantidad);
  });

  const engancheEnPagos = engancheEnPagosAcum > 0;
  // Sprint ENG-FIX (backwards-compat): tri-estado de engancheCobradoFuera.
  //   true       → cobrado fuera (suma)
  //   false      → pendiente explícito (no suma)
  //   undefined  → legacy (suma, comportamiento previo)
  const engancheCuenta = deuda?.engancheCobradoFuera !== false;
  const engancheCobradoFuera = deuda?.engancheCobradoFuera === true;
  if (!engancheEnPagos && engancheCuenta) {
    totalPagadoVenta += n(deuda?.enganche);
  }

  const engancheCobrado = engancheEnPagos
    ? engancheEnPagosAcum
    : (engancheCuenta ? n(deuda?.enganche) : 0);
  const saldoRestante = Math.max(montoTotal - totalPagadoVenta, 0);

  return {
    montoTotal,
    enganche: n(deuda?.enganche),
    engancheCobrado,
    mensualidadesCobradas: mensualidadesAcum,
    engEnPagos: engancheEnPagos,
    mensualidad: n(deuda?.mensualidad),
    mesesAcordados: n(
      deuda?.plazo ??
        deuda?.meses ??
        deuda?.mesesAcordados ??
        deuda?.plazoMeses ??
        deuda?.duracionMeses
    ),
    totalPagadoVenta,
    totalDevuelto,
    saldoRestante,
    engancheCobradoFuera,
  };
};

const mesesAcordadosTexto = (meses = 0) => {
  const total = Number(meses || 0);
  if (!Number.isFinite(total) || total <= 0) return "—";
  return `${total} ${total === 1 ? "mes" : "meses"}`;
};

const addLogoHeader = async (doc, titulo) => {
  const logo = await loadLogo();

  if (logo) {
    const logoW = 70;
    const logoH = (logo.height * logoW) / logo.width;
    const centerX = 306 - logoW / 2;

    doc.addImage(logo, "PNG", centerX, 24, logoW, logoH);
    doc.setFontSize(20);
    doc.text(titulo, 306, 90 + logoH, { align: "center" });

    doc.setLineWidth(1.5);
    doc.line(80, 100 + logoH, 532, 100 + logoH);

    return 120 + logoH;
  }

  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("GRUPO INMOBILIARIO K&E", 306, 50, { align: "center" });

  doc.setFontSize(20);
  doc.text(titulo, 306, 78, { align: "center" });
  doc.line(80, 90, 532, 90);

  return 110;
};

export const generarEstadoCuentaPDF = async ({
  deuda,
  cliente,
  clienteId,
  deudaId,
  promesasPago = [],
  gestionesCobranza = [],
  perfilCobranza = null,
}) => {
  const fin = computeFinanzas(deuda);
  const esDevolucion = isDevolucionDeuda(deuda);

  const doc = new jsPDF({ unit: "pt", format: "letter" });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 34;

  const montoInicial = esDevolucion ? fin.baseDev : fin.montoTotal;
  const saldoFinal = esDevolucion ? fin.pendienteDev : fin.saldoRestante;
  const totalMostrado = esDevolucion
    ? fin.totalDevuelto
    : fin.totalPagadoVenta;

  const avance =
    montoInicial > 0
      ? Math.min((totalMostrado / montoInicial) * 100, 100)
      : 0;

let y = 28;

// LOGO IZQUIERDA (PEQUEÑO)
try {
  doc.addImage(logo, "PNG", margin, y, 42, 42);
} catch (e) {
  console.warn("Logo error:", e);
}

// TÍTULO
doc.setFont("helvetica", "bold");
doc.setFontSize(16);
doc.setTextColor(0);
doc.text("ESTADO DE CUENTA", margin + 50, y + 18);

// LÍNEA
doc.setLineWidth(0.8);


// FECHA
doc.setFont("helvetica", "normal");
doc.setFontSize(7.5);
doc.setTextColor(80);



doc.text(
  `Fecha de emisión: ${new Date().toLocaleString("es-MX")}`,
  margin,
  y + 52
);

y += 62;


y += 2;

  y += 2;

  const pagos = Array.isArray(deuda?.pagos) ? deuda.pagos : [];

  const pagosDesc = [...pagos].sort((a, b) => {
    const fa = toJsDate(a?.fecha)?.getTime() || 0;
    const fb = toJsDate(b?.fecha)?.getTime() || 0;
    return fb - fa;
  });

  const ultimoPago = pagosDesc[0] || null;

  const props = getPropsList(deuda);

  const propiedadResumen =
    props.length > 1
      ? `${props.length} propiedades incluidas en la operación`
      : propsLabel(deuda);

  const topY = y;
const leftW = 260;
const rightW = 510 - leftW;

// TABLA IZQUIERDA: DATOS CLIENTE
autoTable(doc, {
  startY: topY,
  head: [["Datos del cliente"]],
  body: [
    [
      [
        `Cliente: ${clienteNombre(cliente)}`,
        `Teléfono: ${cliente?.telefono || "—"}`,
        `Referencia: ${deuda?.referencia || "—"}`,
        `Estado: ${esDevolucion ? "Devolución" : "Venta activa"}`,
      ].join("\n"),
    ],
  ],
  theme: "grid",
  styles: {
    fontSize: 7.3,
    cellPadding: 3,
    valign: "top",
    overflow: "linebreak",
  },
  headStyles: {
    fillColor: [35, 84, 126],
    textColor: 255,
    fontStyle: "bold",
  },
  margin: { left: margin, right: pageWidth - margin - leftW },
  tableWidth: leftW,
});

// TABLA DERECHA: RESUMEN CONTRATO
autoTable(doc, {
  startY: topY,
  head: [["Resumen del contrato"]],
  body: [
    [`Monto total: ${money(montoInicial)}`],
    [`Fecha de inicio: ${fechaContrato(deuda)}`],
    ...(!esDevolucion ? [[`Enganche: ${money(fin.enganche)}`]] : []),
    ...(!esDevolucion ? [[`Mensualidad: ${money(fin.mensualidad)}`]] : []),
    ...(!esDevolucion ? [[`Meses acordados: ${mesesAcordadosTexto(fin.mesesAcordados)}`]] : []),
    [`${esDevolucion ? "Total devuelto" : "Total pagado"}: ${money(totalMostrado)}`],
    ...(!esDevolucion && (fin.engancheCobrado > 0)
      ? [
          [`   • Enganche cobrado: ${money(fin.engancheCobrado)}`],
          [`   • Mensualidades: ${money(fin.mensualidadesCobradas)}`],
        ]
      : []),
    [
      {
        content: `${esDevolucion ? "Pendiente por devolver" : "Saldo pendiente"}: ${money(saldoFinal)}`,
        styles: {
          fillColor: [255, 245, 157],
          fontStyle: "bold",
          textColor: [20, 20, 20],
        },
      },
    ],
    [`Avance: ${avance.toFixed(1)}%`],
  ],
  theme: "grid",
  styles: {
    fontSize: 7.3,
    cellPadding: 3,
    valign: "top",
    overflow: "linebreak",
  },
  headStyles: {
    fillColor: [31, 41, 55],
    textColor: 255,
    fontStyle: "bold",
  },
  margin: { left: margin + leftW, right: margin },
  tableWidth: rightW,
});

y = Math.max(
  doc.previousAutoTable?.finalY || topY,
  doc.lastAutoTable?.finalY || topY
) + 6;

  y = doc.lastAutoTable.finalY + 5;

  autoTable(doc, {
    startY: y,
    head: [["Información de la propiedad", "Detalle"]],
    body: [
      ["Concepto", deuda?.concepto || "Venta de terreno"],
      ["Propiedad(es)", propiedadResumen || "—"],
      [
        "Detalle",
        props.length > 1
          ? "El detalle completo de propiedades se conserva en el contrato y en el sistema."
          : propsLabel(deuda),
      ],
    ],
    theme: "grid",
    styles: {
      fontSize: 7,
     cellPadding: 2.4,
      valign: "top",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [35, 84, 126],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 7,
      cellPadding: 2.4,
    },
    columnStyles: {
      0: { cellWidth: 130, fontStyle: "bold" },
      1: { cellWidth: 380 },
    },
    margin: { left: margin, right: margin },
  });

  y = doc.lastAutoTable.finalY + 5;

  autoTable(doc, {
    startY: y,
    head: [["Estado de pagos", "Detalle"]],
    body: [
      [
        "Último pago registrado",
        ultimoPago
          ? `${fecha(ultimoPago.fecha)} — ${money(ultimoPago.cantidad)}`
          : "Sin pagos registrados",
      ],
      ["Total de pagos registrados", pagos.length],
      ...(!esDevolucion ? [["Meses acordados del contrato", mesesAcordadosTexto(fin.mesesAcordados)]] : []),
      ["Saldo pendiente", money(saldoFinal)],
    ],
    theme: "grid",
    styles: {
      fontSize: 7,
      cellPadding: 2.4,
      valign: "top",
    },
    headStyles: {
      fillColor: [31, 41, 55],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 7,
      cellPadding: 2.4,
    },
    columnStyles: {
      0: { cellWidth: 165, fontStyle: "bold" },
      1: { cellWidth: 345 },
    },
    margin: { left: margin, right: margin },
  });

  y = doc.lastAutoTable.finalY + 5;

  const pagosAsc = [...pagos].sort((a, b) => {
    const fa = toJsDate(a?.fecha)?.getTime() || 0;
    const fb = toJsDate(b?.fecha)?.getTime() || 0;
    return fa - fb;
  });

const columnas = 3;
const filasPorColumna = Math.ceil(pagosAsc.length / columnas);

const buildPago = (p, index) => {
  if (!p) return ["", "", ""];

  return [index + 1, fecha(p.fecha), signedMoney(p?.cantidad)];
};

const pagosTresColumnas = Array.from({ length: filasPorColumna }).map((_, i) => {
  const p1 = pagosAsc[i];
  const p2 = pagosAsc[i + filasPorColumna];
  const p3 = pagosAsc[i + filasPorColumna * 2];

  return [
    ...buildPago(p1, i),
    ...buildPago(p2, i + filasPorColumna),
    ...buildPago(p3, i + filasPorColumna * 2),
  ];
});

if (pagosTresColumnas.length > 0) {
  const tablaPagosStartY = y;
  const bloquePagoWidth = 18 + 105 + 56;

  // El pie de la tabla suma SOLO lo que está en la tabla (las filas de pagos).
  // No incluye el enganche del doc cuando no aparece como pago, para que el
  // total visible cuadre con la suma de las filas listadas.
  const totalTablaPagos = pagosAsc.reduce(
    (acc, p) => acc + n(p?.cantidad),
    0
  );

  autoTable(doc, {
    startY: y,
    head: [
      [
        "#", "Fecha", "Monto",
        "#", "Fecha", "Monto",
        "#", "Fecha", "Monto",
      ],
    ],
    body: pagosTresColumnas,
    foot: [
      [
        "", "TOTAL", money(totalTablaPagos),
        "", "", "",
        "", "", "",
      ],
    ],
    theme: "striped",
    styles: {
      fontSize: 8.2,
      cellPadding: 1.8,
      overflow: "hidden",
      minCellHeight: 9,
      valign: "middle",
    },
    headStyles: {
      fillColor: [35, 84, 126],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 8.3,
      cellPadding: 1.8,
    },
    footStyles: {
      fillColor: [235, 240, 245],
      textColor: 20,
      fontStyle: "bold",
      fontSize: 8.3,
      cellPadding: 1.8,
    },
    columnStyles: {
      0: { cellWidth: 18, halign: "center" },
      1: { cellWidth: 105 },
      2: { cellWidth: 56, halign: "right", lineWidth: { right: 1.2 }, lineColor: [35, 84, 126] },

      3: { cellWidth: 18, halign: "center" },
      4: { cellWidth: 105 },
      5: { cellWidth: 56, halign: "right", lineWidth: { right: 1.2 }, lineColor: [35, 84, 126] },

      6: { cellWidth: 18, halign: "center" },
      7: { cellWidth: 105 },
      8: { cellWidth: 56, halign: "right", lineWidth: { right: 0.8 }, lineColor: [35, 84, 126] },
    },
    didParseCell: (data) => {
      if (![2, 5, 8].includes(data.column.index)) return;
      data.cell.styles.lineColor = [35, 84, 126];
      data.cell.styles.lineWidth = {
        ...data.cell.styles.lineWidth,
        right: data.column.index === 8 ? 0.8 : 1.2,
      };
    },
    didDrawCell: (data) => {
      if (![2, 5, 8].includes(data.column.index)) return;

      const { cell } = data;
      const x = cell.x + cell.width;
      doc.setDrawColor(35, 84, 126);
      doc.setLineWidth(data.column.index === 8 ? 0.7 : 1.1);
      doc.line(x, cell.y, x, cell.y + cell.height);
      doc.setLineWidth(0.2);
    },
    margin: { left: margin, right: margin },
    pageBreak: "auto",
    rowPageBreak: "avoid",
  });

  const tablaPagosEndY = doc.lastAutoTable.finalY;
  doc.setDrawColor(35, 84, 126);
  [1, 2, 3].forEach((bloque) => {
    const x = margin + bloquePagoWidth * bloque;
    doc.setLineWidth(bloque === 3 ? 0.8 : 1.25);
    doc.line(x, tablaPagosStartY, x, tablaPagosEndY);
  });
  doc.setLineWidth(0.2);

  y = doc.lastAutoTable.finalY + 6;
}

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(90);

  const nota = doc.splitTextToSize(
    "Este estado de cuenta es informativo y refleja los pagos registrados en el sistema a la fecha de emisión. Para cualquier aclaración o actualización, favor de comunicarse con el área administrativa.",
    pageWidth - margin * 2
  );

  doc.text(nota, margin, y + 8);

  doc.setTextColor(0);
  doc.setFontSize(7);
  doc.setFont("helvetica", "italic");
  doc.text("Grupo Inmobiliario K&E", pageWidth / 2, pageHeight - 22, {
    align: "center",
  });

  doc.save(`EstadoCuenta_${clienteId || "cliente"}_${deudaId || "deuda"}.pdf`);
};
export const generarConstanciaLiquidacionPDF = async ({
  cliente,
  deuda,
  clienteId,
  deudaId,
  fecha,
  tratamiento,
}) => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const logo = await loadLogo();
  const pageWidth = doc.internal.pageSize.getWidth();
  const centerX = pageWidth / 2;

  // Encabezado: logo arriba a la izquierda, fecha arriba a la derecha.
  // Cap de altura en 95 pt para que el logo no se sobreponga con el
  // bloque de texto de la inmobiliaria (que arranca en y=170).
  if (logo) {
    const MAX_W = 110;
    const MAX_H = 95;
    const ratio = logo.width / logo.height;
    let logoW = MAX_W;
    let logoH = MAX_W / ratio;
    if (logoH > MAX_H) {
      logoH = MAX_H;
      logoW = MAX_H * ratio;
    }
    doc.addImage(logo, "PNG", 40, 35, logoW, logoH);
  }

  const fechaConstancia =
    fecha instanceof Date && !Number.isNaN(fecha.getTime())
      ? fecha
      : new Date();
  const fechaTexto = fechaConstancia.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  doc.setFontSize(12);
  doc.setFont(undefined, "normal");
  doc.text(`Nahuatzen Mich., a ${fechaTexto}.`, pageWidth - 40, 65, {
    align: "right",
  });

  // Datos de la inmobiliaria (cabecera fija)
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.text("“INMOBILIARIA KUMANCHIKUA ECHERI”", 40, 170);

  doc.setFontSize(11);
  doc.setFont(undefined, "normal");
  doc.text(
    [
      "Calle Amado Nervo # 444 Barrio Primero",
      "RFC: IKE2208085U6",
      "C.P 60280, Nahuatzen Michoacán",
      "Cel. 452 343 0817",
      "kyeinmobiliaria@outlook.com",
    ],
    40,
    188
  );

  // Título centrado con espaciado tipo "C O N S T A N C I A   D E   L I Q U I D A C I Ó N"
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text(
    "C O N S T A N C I A   D E   L I Q U I D A C I Ó N",
    centerX,
    285,
    { align: "center" }
  );

  // Cuerpo
  doc.setFontSize(12);
  doc.setFont(undefined, "normal");

  const nombreCliente = clienteNombre(cliente);
  const referencia = deuda?.referencia || "—";
  const trato = tratamiento || "C.";
  const props = Array.isArray(deuda?.propiedades) ? deuda.propiedades : [];
  const descripcionPropiedades = describirPropiedadesPorManzana(
    props.length ? props : [deuda]
  );
  const { nombre: fraccNombre, ubicacion: fraccUbicacion } =
    await resolverFraccionamientoConUbicacion(deuda);

  // Frase (PDF MOD): "Por medio del presente documento se hace constar que
  // {tratamiento} {Nombre} ha cubierto en su totalidad el adeudo referente a
  // {lotes} del fraccionamiento "X", ubicado en {ubicación}. Referencia
  // número {ref}."  La REFERENCIA va al FINAL.
  const partesCuerpo = [];
  partesCuerpo.push(
    `Por medio del presente documento se hace constar que ${trato} ${nombreCliente} ha cubierto en su totalidad el adeudo referente a ${
      descripcionPropiedades || "sus propiedades"
    }`
  );
  if (fraccNombre) {
    partesCuerpo.push(`del fraccionamiento “${fraccNombre}”`);
  }
  if (fraccUbicacion) {
    partesCuerpo.push(`, ubicado en ${fraccUbicacion}`);
  }
  // Une el cuerpo cuidando que el segmento de ubicación (que empieza con coma)
  // no quede precedido de un espacio extra antes de la coma.
  let cuerpoCompleto = partesCuerpo
    .join(" ")
    .replace(/\s+,/g, ",")
    .replace(/\s{2,}/g, " ")
    .trim();
  // Cierre con punto + referencia al final.
  cuerpoCompleto = `${cuerpoCompleto.replace(/[.\s]+$/, "")}. Referencia número ${referencia}.`;
  const lineas = doc.splitTextToSize(cuerpoCompleto, pageWidth - 80);
  doc.text(lineas, 40, 340, { align: "justify", maxWidth: pageWidth - 80 });

  // Cierre de cortesía
  const yCierre = 340 + lineas.length * 16 + 30;
  doc.text("Sin más por el momento y agradeciendo su atención.", 40, yCierre);

  // ATENTAMENTE + firma centradas
  const yAtentamente = yCierre + 60;
  doc.setFont(undefined, "normal");
  doc.text("A T E N T A M E N T E", centerX, yAtentamente, { align: "center" });

  // Espacio para firma manual
  const yLineaFirma = yAtentamente + 70;
  doc.text("_________________________________", centerX, yLineaFirma, {
    align: "center",
  });
  doc.text("C. ALBA HELENA GALINZOGA SOLORZANO", centerX, yLineaFirma + 18, {
    align: "center",
  });
  doc.text("REPRESENTANTE DE LA", centerX, yLineaFirma + 36, { align: "center" });
  doc.text(
    "“INMOBILIARIA KUMANCHIKUA ECHERI”",
    centerX,
    yLineaFirma + 54,
    { align: "center" }
  );

  doc.save(
    `Constancia_Liquidacion_${clienteId || "cliente"}_${deudaId || "deuda"}.pdf`
  );
};

export const generarComprobantePagoPDF = async ({
  pago,
  cliente,
  deuda,
  deudaId,
}) => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const logo = await loadLogo();

  if (logo) {
    doc.addImage(logo, "PNG", 40, 30, 80, (logo.height * 80) / logo.width);
  }

  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  doc.text("RECIBO DE PAGO", 460, 50, { align: "right" });

  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.text(new Date().toLocaleString("es-MX"), 460, 68, { align: "right" });

  const rows = [
    ["Cliente", clienteNombre(cliente)],
    ["Deuda", deuda?.folio || deudaId || "—"],
    ["Concepto", deuda?.concepto || "—"],
    ["Propiedad(es)", propsLabel(deuda)],
    ["Pago ID", pago?.pagoId || "—"],
    ["Fecha de pago", fechaHora(pago?.fecha)],
    ["Método", pago?.metodoEtiqueta || metodoPagoLabel(pago?.metodo) || "—"],
    ["Cobrador", pago?.cobradorNombre || "—"],
    ["Referencia", pago?.referencia || "—"],
    ["Notas", pago?.notas || "—"],
    ["Monto", money(Math.abs(n(pago?.cantidad)))],
  ];

  autoTable(doc, {
    startY: 110,
    head: [["Campo", "Valor"]],
    body: rows,
    styles: { fontSize: 10 },
    headStyles: {
      fillColor: [35, 84, 126],
      textColor: 255,
      fontStyle: "bold",
    },
    margin: { left: 40, right: 40 },
  });

  doc.setFontSize(9);
  doc.text(
    "Este documento es un comprobante informativo interno.",
    40,
    doc.internal.pageSize.getHeight() - 40
  );

  doc.save(`recibo_${pago?.pagoId || "pago"}.pdf`);
};

export const generarTicketPagoPDF = async ({
  pago,
  cliente,
  deuda,
  deudaId,
}) => {
  const pageWidth = 164;
  const pageHeight = 400;
  const margin = 6;
  const centerX = pageWidth / 2;

  const doc = new jsPDF({
    unit: "pt",
    format: [pageWidth, pageHeight],
  });

  const logo = await loadLogo();

  if (logo) {
    const logoW = 40;
    const logoH = (logo.height * logoW) / logo.width;
    doc.addImage(logo, "PNG", centerX - logoW / 2, margin, logoW, logoH);
  }

  let y = 62;

  doc.setFontSize(9);
  doc.setFont(undefined, "bold");
  doc.text("GRUPO K&E", centerX, y, { align: "center" });
  y += 12;

  doc.setFontSize(8);
  doc.text("COMPROBANTE DE PAGO", centerX, y, { align: "center" });
  y += 12;

  doc.setLineWidth(0.4);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;

  doc.setFontSize(7);
  doc.setFont(undefined, "normal");

  const lines = [
    `Cliente: ${clienteNombre(cliente)}`,
    `Deuda: ${deuda?.folio || deudaId || "—"}`,
    `Concepto: ${deuda?.concepto || "—"}`,
    `Propiedad: ${propsLabel(deuda)}`,
    `Fecha: ${fechaHora(pago?.fecha)}`,
    `Método: ${pago?.metodoEtiqueta || metodoPagoLabel(pago?.metodo) || "—"}`,
    `Monto: ${money(Math.abs(n(pago?.cantidad)))}`,
  ];

  lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, pageWidth - margin * 2);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 9 + 2;
  });

  doc.line(margin, y + 8, pageWidth - margin, y + 8);
  y += 22;

  doc.setFontSize(6);
  doc.text("Documento informativo interno", centerX, y, { align: "center" });

  doc.save(`ticket_${pago?.pagoId || "pago"}.pdf`);
};

void addLogoHeader;
